// CredentialPage — /credentials/:slug
// THE public credential page: what an employer, university, or NGO sees when
// they scan the QR code on a resume or certificate.
//
// DESIGN LAW: every certificate displays with EQUAL visual weight — no tier
// badges, no tier ordering, no "this is a lower-level course" signals.
// A church learner's Bible Journey cert looks exactly as substantial as a
// university degree. The course NAME is the identity. Sorted by date.
//
// TWO TIERS:
//   Public (no PIN): cert list — course name, mastery score, date, "Verified"
//   Deep (PIN): full evidence — photo, quiz history, retries, all certs
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Award, ShieldCheck, Loader2, AlertTriangle, Lock, Eye, ChevronDown,
  TrendingUp, RotateCcw, Clock, X,
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import SEO from '@/components/layout/SEO'
import { useTranslation } from 'react-i18next';

const M = '#06060c'
const D2 = '#0a0a14'
const CARD = '#0d0d15'
const BLUE = '#0ea5e9'
const G = '#d4a843'
const GR = '#4ade80'
const BR = 'rgba(255,255,255,0.10)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const MU2 = 'rgba(255,255,255,0.35)'
const serif = "'Playfair Display', Georgia, serif"
const sans = "'Inter', system-ui, sans-serif"

interface PublicCert {
  id: string
  course_name: string
  course_icon: string
  mastery_score: number | null
  issued_at: string
  cert_type: string
  is_publicly_visible?: boolean
}

interface DeepCert extends PublicCert {
  is_publicly_visible: boolean
  quiz_history: {
    lesson_unit: number
    lesson_number: number
    score_percentage: number
    attempted_at: string
  }[]
}

interface LearnerInfo {
  credential_name: string
  identity_verified: boolean
  photo_url?: string | null
}

export default function CredentialPage() {
  const { t } = useTranslation();

  const { slug } = useParams<{ slug: string }>()
  const [status, setStatus] = useState<'loading' | 'public' | 'deep' | 'notfound' | 'ratelimited'>('loading')
  const [learner, setLearner] = useState<LearnerInfo | null>(null)
  const [certs, setCerts] = useState<PublicCert[]>([])
  const [deepCerts, setDeepCerts] = useState<DeepCert[]>([])
  const [pin, setPin] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const [pinLoading, setPinLoading] = useState(false)
  const [expandedCertId, setExpandedCertId] = useState<string | null>(null)

  // ── Load public tier on mount ──
  useEffect(() => {
    if (!slug) return
    let cancelled = false

    supabase.functions.invoke('get-verify-data', {
      body: { slug },
    }).then(({ data }: any) => {
      if (cancelled) return
      if (data?.error === 'not_found') {
        setStatus('notfound')
        return
      }
      if (data?.success && data?.tier === 'public') {
        setLearner(data.learner)
        setCerts(data.certificates || [])
        setStatus('public')
      }
    }).catch(() => {
      if (!cancelled) setStatus('notfound')
    })

    return () => { cancelled = true }
  }, [slug])

  // ── PIN submission → deep tier ──
  const submitPin = useCallback(async () => {
    if (!slug || pin.length !== 4) return
    setPinLoading(true)
    setPinError(null)

    try {
      const { data } = await supabase.functions.invoke('get-verify-data', {
        body: { slug, pin },
      })

      if (data?.error === 'invalid_pin') {
        setPinError('Incorrect PIN. Please check with the learner.')
      } else if (data?.error === 'rate_limited') {
        setPinError('Too many attempts. Please wait 10 minutes.')
        setStatus('ratelimited')
      } else if (data?.error === 'no_pin_set') {
        setPinError('This learner has not set a verification PIN yet.')
      } else if (data?.success && data?.tier === 'deep') {
        setLearner(data.learner)
        setDeepCerts(data.certificates || [])
        setStatus('deep')
      } else {
        setPinError('Something went wrong. Please try again.')
      }
    } catch {
      setPinError('Network error. Please try again.')
    } finally {
      setPinLoading(false)
    }
  }, [slug, pin])

  // ── Render helpers ──
  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

  // ── Loading state ──
  if (status === 'loading') {
    return (
      <div style={{ background: M, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={28} color={BLUE} className="animate-spin" />
      </div>
    )
  }

  // ── Not found ──
  if (status === 'notfound') {
    return (
      <div style={{ background: M, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <SEO title="Learner Not Found | Still Waters Academy" noindex />
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <AlertTriangle size={40} color="#dc2626" style={{ margin: '0 auto 16px' }} />
          <h1 style={{ fontFamily: serif, fontSize: 26, color: TX, margin: '0 0 10px' }}>{t('credential.learner_not_found')}</h1>
          <p style={{ color: MU, fontSize: 14, lineHeight: 1.6 }}>
            No public credential page exists at this address. Check the link or QR code,
            or contact Still Waters Academy.
          </p>
          <Link to="/" style={{ color: G, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
            stillwatersacademy.com →
          </Link>
        </div>
      </div>
    )
  }

  const displayName = learner?.credential_name || 'Learner'
  const isDeep = status === 'deep'
  const displayCerts: (PublicCert | DeepCert)[] = isDeep ? deepCerts : certs

  // ═══ SMART TIER DETECTION ═══
  // Single-domain learners see a flat list (all equal — a church-only
  // learner's page looks exactly as substantial as anyone's). Multi-domain
  // learners see grouped with tier ordering (they've earned that story).
  const detectDisplayMode = (certList: (PublicCert | DeepCert)[]): 'flat' | 'grouped' => {
    if (certList.length <= 1) return 'flat'
    const certTypes = new Set(certList.map(c => c.cert_type))
    return certTypes.size === 1 ? 'flat' : 'grouped'
  }

  const TIER_ORDER: Record<string, number> = {
    'university': 0,
    'sage': 1,
  }

  const sortCertsForGrouped = (certList: (PublicCert | DeepCert)[]) => {
    return [...certList].sort((a, b) => {
      const tierA = TIER_ORDER[a.cert_type] ?? 99
      const tierB = TIER_ORDER[b.cert_type] ?? 99
      if (tierA !== tierB) return tierA - tierB
      return new Date(b.issued_at).getTime() - new Date(a.issued_at).getTime()
    })
  }

  const displayMode = detectDisplayMode(displayCerts)
  const sortedCerts = displayMode === 'grouped' ? sortCertsForGrouped(displayCerts) : displayCerts

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: sans }}>
      <SEO
        title={`${displayName} — Verified Credentials | Still Waters Academy`}
        description={`Verified learning credentials for ${displayName}, issued by Still Waters Academy.`}
      />

      {/* ═══ VERIFIED BANNER ═══ */}
      <div style={{ background: 'rgba(14,165,233,0.08)', borderBottom: '1px solid rgba(14,165,233,0.25)', padding: '14px 20px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: BLUE, fontWeight: 800, fontSize: 13 }}>
          <ShieldCheck size={16} /> Verified by Still Waters Academy
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 20px 80px' }}>

        {/* ═══ LEARNER HEADER ═══ */}
        <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 24, padding: 32, marginBottom: 24, textAlign: 'center' }}>
          {isDeep && learner?.photo_url ? (
            <img
              src={learner.photo_url}
              alt={displayName}
              style={{ width: 96, height: 96, borderRadius: 20, objectFit: 'cover', border: '2px solid rgba(74,222,128,0.3)', margin: '0 auto 16px', display: 'block' }}
            />
          ) : (
            <div style={{ width: 96, height: 96, borderRadius: 20, background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 36, color: MU2, fontFamily: serif }}>
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <h1 style={{ fontFamily: serif, fontSize: 'clamp(1.8rem, 5vw, 2.4rem)', fontWeight: 700, margin: 0, color: TX }}>
            {displayName}
          </h1>
          <p style={{ color: MU, fontSize: 14, margin: '8px 0 0' }}>
            {displayCerts.length} verified credential{displayCerts.length === 1 ? '' : 's'} · Still Waters Academy
          </p>
          {learner?.identity_verified && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 14, padding: '4px 12px', borderRadius: 999, background: 'rgba(74,222,128,0.12)', color: GR, fontSize: 11, fontWeight: 800 }}>
              <ShieldCheck size={12} /> Identity verified
            </div>
          )}
        </div>

        {/* ═══ PIN GATE (public tier only) ═══ */}
        {!isDeep && status === 'public' && (
          <div style={{ background: D2, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 28px', marginBottom: 24, textAlign: 'center' }}>
            <Lock size={20} color={G} style={{ margin: '0 auto 12px', display: 'block' }} />
            <p style={{ color: MU, fontSize: 13, lineHeight: 1.6, margin: '0 0 16px' }}>
              This learner has a deeper verified record — every assessment, every score, every retry.
              Enter their verification PIN to view the full learning evidence.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={(e) => e.key === 'Enter' && submitPin()}
                placeholder="••••"
                style={{
                  width: 120, padding: '14px 18px', borderRadius: 12, border: `2px solid ${pin ? 'rgba(74,222,128,0.4)' : BR}`,
                  background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 24, textAlign: 'center',
                  fontFamily: "'Courier New', monospace", fontWeight: 700, letterSpacing: '8px',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button
                onClick={submitPin}
                disabled={pinLoading || pin.length !== 4}
                style={{
                  padding: '14px 24px', borderRadius: 12, border: 'none', cursor: pin.length === 4 ? 'pointer' : 'not-allowed',
                  background: pin.length === 4 ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.05)',
                  color: pin.length === 4 ? M : MU, fontWeight: 800, fontSize: 14,
                }}
              >
                {pinLoading ? '…' : 'Unlock'}
              </button>
            </div>
            {pinError && <p style={{ color: '#f87171', fontSize: 12, marginTop: 12, marginBottom: 0 }}>{pinError}</p>}
          </div>
        )}

        {/* ═══ CERTIFICATE LIST ═══ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {sortedCerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', background: CARD, borderRadius: 20, border: `1px dashed ${BR}` }}>
              <Award size={32} color={MU2} style={{ margin: '0 auto 12px', display: 'block' }} />
              <p style={{ color: MU, fontSize: 14 }}>{t('credential.no_public_credentials_yet')}</p>
            </div>
          ) : (
            sortedCerts.map((cert, idx) => {
              const prevCert = idx > 0 ? sortedCerts[idx - 1] : null
              const isNewTier = displayMode === 'grouped' && prevCert && prevCert.cert_type !== cert.cert_type
              const isExpanded = expandedCertId === cert.id
              const deep = cert as DeepCert
              const hasEvidence = isDeep && deep.quiz_history && deep.quiz_history.length > 0

              return (
                <React.Fragment key={cert.id}>
                  {isNewTier && (
                    <div style={{ paddingTop: 16, paddingBottom: 4 }}>
                      <p style={{
                        fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.35)',
                        textTransform: 'uppercase', letterSpacing: '0.15em', margin: 0,
                      }}>
                        {cert.cert_type === 'university' ? 'University Credentials' : 'Academy Credentials'}
                      </p>
                    </div>
                  )}
                  <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, overflow: 'hidden' }}>
                {/* Cert card header */}
                <div
                  onClick={() => hasEvidence && setExpandedCertId(isExpanded ? null : cert.id)}
                  style={{
                    padding: '22px 24px', display: 'flex', alignItems: 'center', gap: 16,
                    cursor: hasEvidence ? 'pointer' : 'default',
                  }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 14,
                    background: 'rgba(212,168,67,0.08)', border: '1px solid rgba(212,168,67,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0,
                  }}>
                    {cert.course_icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontFamily: serif, fontSize: 17, fontWeight: 700, color: TX, margin: 0, lineHeight: 1.3 }}>
                      {cert.course_name}
                    </h3>
                    <p style={{ fontSize: 12, color: MU2, margin: '4px 0 0' }}>
                      Issued {fmtDate(cert.issued_at)}
                      {isDeep && !cert.is_publicly_visible && (
                        <span style={{ color: MU2, marginLeft: 8, fontStyle: 'italic' }}>(private)</span>
                      )}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{
                      fontFamily: serif, fontSize: 26, fontWeight: 800,
                      color: (cert.mastery_score ?? 0) >= 90 ? GR : G,
                    }}>
                      {cert.mastery_score ?? '—'}%
                    </div>
                    <div style={{ fontSize: 9, color: MU2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Mastery
                    </div>
                  </div>
                  {hasEvidence && (
                    <ChevronDown size={16} color={MU} style={{
                      flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
                    }} />
                  )}
                </div>

                {/* Expanded evidence (deep tier only) */}
                {isExpanded && hasEvidence && (
                  <div style={{ borderTop: `1px solid ${BR}`, padding: '18px 24px' }}>
                    <p style={{ fontSize: 10, fontWeight: 900, color: G, letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px' }}>
                      Assessment history — {deep.quiz_history.length} recorded
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {deep.quiz_history.map((q, qi) => (
                        <div key={qi} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: MU, padding: '6px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8 }}>
                          <span>Unit {q.lesson_unit} · Lesson {q.lesson_number} · {fmtDate(q.attempted_at)}</span>
                          <span style={{ fontWeight: 800, color: q.score_percentage >= 80 ? GR : '#fbbf24' }}>
                            {q.score_percentage}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          )
        })
      )}
        </div>

        {/* ═══ INSTITUTIONAL FOOTER ═══ */}
        <div style={{ textAlign: 'center', marginTop: 40, padding: '0 20px' }}>
          <p style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: TX, margin: '0 0 6px' }}>
            Still Waters Academy
          </p>
          <p style={{ color: MU2, fontSize: 11, lineHeight: 1.6, margin: 0, maxWidth: 480, marginInline: 'auto' }}>
            Every credential is backed by a permanent, system-generated learning record —
            lessons completed, concepts mastered, assessments scored server-side, retries documented.
            Credential names are locked at issuance; changes require written request to the Academy.
          </p>
          <Link to="/" style={{ color: G, fontWeight: 700, fontSize: 13, textDecoration: 'none', display: 'inline-block', marginTop: 12 }}>
            stillwatersacademy.com
          </Link>
        </div>
      </div>
    </div>
  )
}